<template>
  <BaseCard noPadding>
    <div :class="$style.base">
      <BaseIconTile
        v-if="unleash.isEnabled('ServicePackages') && isPackage"
        name="cube"
        fullHeight
        v-test="'appointment-part-icon'"
      />
      <div :class="$style.main">
        <div :class="$style.line">
          <BaseText bold>
            {{ name }}
          </BaseText>
          <BaseText
            iconBefore="clock"
            :class="$style.duration"
          >
            {{ `${duration} ${$t('global.minute_short')}` }}
          </BaseText>
        </div>
        <div
          v-if="isPackage"
          :class="$style.packageParts"
        >
          <div
            v-for="(service, index) in services"
            :key="`service-${index}`"
            :class="$style.line"
            v-test="'appointment-package-part'"
          >
            <BaseText color="secondary">
              {{ service.name }}
            </BaseText>
            <BaseText
              color="secondary"
              :class="$style.duration"
            >
              {{ `${service.duration} ${$t('global.minute_short')}` }}
            </BaseText>
          </div>
        </div>
        <div
          v-if="!hasSingleResource && resources.length"
          :class="$style.resources"
        >
          <div
            v-for="resource in resources"
            :key="resource.id"
            :class="$style.resource"
            v-test="'appointment-part-resource'"
          >
            <div :class="$style.resourceImage">
              <BaseAvatar
                v-if="resource.type === 'employee'"
                :resource="resource"
              />
              <BaseIcon
                v-if="resource.type === 'room'"
                name="pin"
              />
              <BaseIcon
                v-if="resource.type === 'equipment'"
                name="monitor"
              />
            </div>
            <div>{{ resource.name }}</div>
          </div>
        </div>
      </div>
    </div>
  </BaseCard>
</template>

<script
  lang="ts"
  setup
>
import { useResourcesStore } from '@/stores/resources';
import type { Resource, ServicePackage } from '@/types';
import unleash from '@/unleash';

const props = defineProps<{
  parts: any[];
  servicePackage?: ServicePackage;
}>();

const { hasSingleResource } = useResourcesStore();

const isPackage = computed(() => !!props.servicePackage);

const name = computed(
  () => props.servicePackage?.name ?? props.parts[0].service.name,
);

const duration = computed(() =>
  props.parts.reduce((sum, part) => sum + part.duration, 0),
);

const services = computed(() =>
  props.parts.map((part) => ({
    name: part.service.name,
    duration: part.duration,
  })),
);

const resources = computed(() => {
  const resourcesById = new Map<Resource['id'], Resource>();
  props.parts.forEach((part) => {
    part.resources?.forEach((resource: Resource) => {
      resourcesById.set(resource.id, resource);
    });
  });

  return [...resourcesById.values()].sort((a, b) =>
    a.type === 'employee' && b.type !== 'employee' ? -1 : 1,
  );
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  gap: $spacing * 0.75;
  padding: $spacing * 0.75;
}

.main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: $spacing * 0.5;
}

.line {
  display: flex;
  gap: $spacing;
  justify-content: space-between;
}

.duration {
  flex-shrink: 0;
}

.packageParts {
  display: flex;
  flex-direction: column;
  gap: $spacing * 0.25;
}

.resources {
  display: flex;
  gap: $spacing;
  flex-wrap: wrap;
}

.resource {
  display: flex;
  align-items: center;
}

.resourceImage {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: $spacing * 0.25;
}
</style>
